/* eslint import/no-webpack-loader-syntax: off */
import {
  Dropdown, DropdownContent, DropdownItem, DropdownMenu, Icon
} from 'bloomer'
import { useCallback, useEffect, useRef, useState } from 'react'
import { isMobileOnly, MobileOnlyView } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import SVG from 'react-inlinesvg'
import OutsideClickHandler from 'react-outside-click-handler'
import { useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router'
import { Link } from 'react-router-dom'

// we use react-inlinesvg to be able to change the appearance of the vector icons by css rules
// react-inlinesvg uses fetch requests to obtain the files
// since in Cordova it uses the file:// protocol and Android denies this
// for security reasons, which causes that the icons are not shown in the Android Cordova app.
// we import the Icons as data uris as a workaround to fix this.
import clsx from 'clsx'
import { Badge } from '@mui/material'
import { ICorporationSetting } from '../../api/types/corporation'
import { fallbackCorporationSetting } from '../../constants'
import { useCurrentUser } from '../../hooks/authHooks'
import useChatContext from '../../hooks/useChatContext'
import useIsOffline from '../../hooks/useIsOffline'
import CheckImage from '../../images/check.png'
import { IAlphaprocessState } from '../../store'
import calendarIcon from '../../images/menu-icons/calendar.svg'
import chatIcon from '../../images/menu-icons/chat.svg'
import checksIcon from '../../images/menu-icons/checks.svg'
import companiesIcon from '../../images/menu-icons/companies.svg'
import contactsIcon from '../../images/menu-icons/contacts.svg'
import peopleIcon from '../../images/menu-icons/people.svg'
import resourcesIcon from '../../images/menu-icons/resources.svg'
import tasksIcon from '../../images/menu-icons/tasks.svg'
import todoIcon from '../../images/menu-icons/todo.svg'
import supportIcon from '../../images/menu-icons/support.svg'
import wikiIcon from '../../images/menu-icons/wiki.svg'
import { isCordova } from '../../util'

export default function BottomMenu() {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const { push } = useHistory()
  const user = useCurrentUser()
  const isOffline = useIsOffline()
  const [isOpen, setIsOpen] = useState(false)

  const corporationSettings: ICorporationSetting = useSelector(
    (state: IAlphaprocessState) => state.core.user?.corporation?.corporation_setting
  )
    || fallbackCorporationSetting

  const onClickOutside = useCallback((event) => {
    event.preventDefault()
    setIsOpen(false)
  }, [])

  const { totalUnreadMessages } = useChatContext()

  const closeMenu = useCallback(() => setIsOpen(false), [])

  // While the dropdown is open, disable pointer events on #root-container so that
  // page content (scrollable lists, cards) cannot steal touches from dropdown items
  // in Safari — Safari routes taps to scrollable content behind overlays regardless
  // of z-index when pointer-events aren't explicitly blocked.
  // BottomMenu is rendered OUTSIDE #root-container in App.tsx, so the menu itself
  // is unaffected by this rule.
  // Marks that the floating bottom bar is present (mobile + logged in), so fixed elements
  // like the "Prozess abschließen" button (.sticky-button) can sit above it.
  // Without the bar (e.g. shared link without login) they stick to the screen bottom.
  useEffect(() => {
    document.body.classList.toggle('has-bottom-menu', isMobileOnly)
    return () => { document.body.classList.remove('has-bottom-menu') }
  }, [])

  useEffect(() => {
    document.body.classList.toggle('bottom-menu-open', isOpen)
    return () => { document.body.classList.remove('bottom-menu-open') }
  }, [isOpen])

  // Measure the available viewport height above the dropdown trigger each time it
  // opens so that the dropdown content never extends above the visible area.
  // Pure-CSS viewport units (svh/dvh) are unreliable across Safari versions and
  // browser-chrome states (standalone PWA vs in-browser), so JS measurement is
  // the only robust approach.
  const triggerRef = useRef<HTMLDivElement>(null)
  const [dropdownMaxHeight, setDropdownMaxHeight] = useState<number | undefined>(undefined)

  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      // The CSS positions the dropdown-menu with bottom: (bar-height + 16px) = 80px
      // relative to the trigger div. So the dropdown's bottom edge sits 80px above
      // the trigger's bottom edge in viewport coordinates.
      const dropdownBottomY = rect.bottom - 80
      // Leave 16px breathing room at the top of the viewport.
      const available = dropdownBottomY - 16
      setDropdownMaxHeight(Math.max(200, available))
    } else if (!isOpen) {
      setDropdownMaxHeight(undefined)
    }
  }, [isOpen])

  // Previously the bar was hidden in checklist/area/todo routes (ADF-898) to avoid overlap.
  // With the floating glass bar and the global #root-container padding-bottom fix,
  // content always clears the bar — so we show it everywhere for consistent navigation.

  //const { intercom } = window as any

  // The dropdown is positioned via CSS (bottom: above the glass bar) in _bottomMenu.sass.
  // Do NOT pass an inline top style — Safari's hit-testing can use the inline value
  // even when CSS overrides it with !important, causing taps to fire at the wrong Y.

  return (
    <MobileOnlyView className="bottom-menu">
      <div className="bottom-menu-items">
        <div className="bottom-menu-item">
          {/*<div id="show-support" className="has-pointer" onClick={() => intercom?.displayMessenger()}>
            <SVG src={chatIcon} />
           </div>*/}
          <div id="show-support" className="has-pointer" onClick={() => {
            (window as any).$chatwoot?.toggle()
          }}>
            <SVG src={chatIcon} />
          </div>
        </div>
        <div className="bottom-menu-app-logo">
          <Link to="/" onClick={closeMenu}>
            <div className="clickable-app-logo">
              <img src={CheckImage} alt={t('menu.home')} className="app-logo-icon" />
            </div>
          </Link>
        </div>
        <div className="bottom-menu-item">
          <OutsideClickHandler
            onOutsideClick={onClickOutside}
          >
            <div ref={triggerRef} className="bottom-menu-open" onPointerDown={() => setIsOpen(!isOpen)}>
              <Badge
                badgeContent={totalUnreadMessages}
                color="primary"
                sx={{ '& > .MuiBadge-badge': { right: '-4px', top: '2px' } }}
              >
                <Icon className="fas fa-grip bottom-menu-icon" />
              </Badge>
            </div>

            <Dropdown isAlign="right" isActive={isOpen}>
              <DropdownMenu>
                <DropdownContent style={dropdownMaxHeight !== undefined ? {
                  maxHeight: dropdownMaxHeight,
                  overflowY: 'auto',
                  WebkitOverflowScrolling: 'touch' as const,
                } : undefined}>
                  <DropdownItem
                    isActive={pathname === '/dashboard'}
                    onClick={() => { push('/dashboard'); closeMenu(); }}
                    className="menu-item-with-fa-icon"
                  >
                    <Icon className="fas fa-chart-pie" />
                    <span className="menu-entry-text">
                      { t('menu.dashboard') }
                    </span>
                  </DropdownItem>
                  <DropdownItem
                    isActive={pathname === '/profile'}
                    onClick={() => { push('/profile'); closeMenu(); }}
                  >
                    <SVG src={peopleIcon} />
                    <span className="menu-entry-text">
                      { t('navigation.yourData') }
                    </span>
                  </DropdownItem>
                  <DropdownItem
                    isActive={pathname === '/calendar'}
                    isHidden={!user.has_valid_license}
                    onClick={() => { push('/calendar'); closeMenu(); }}
                  >
                    <SVG src={calendarIcon} />
                    <span className="menu-entry-text">
                      { t('menu.calendar') }
                    </span>
                  </DropdownItem>
                  <DropdownItem
                    isActive={pathname === '/checklists'}
                    onClick={() => { push('/checklists'); closeMenu(); }}
                  >
                    <SVG src={checksIcon} />
                    <span className="menu-entry-text">
                      { t('menu.checklists') }
                    </span>
                  </DropdownItem>
                  <DropdownItem
                    isActive={pathname === '/templates'}
                    onClick={() => { push('/templates'); closeMenu(); }}
                    className="menu-item-with-fa-icon"
                  >
                    <Icon className="far fa-list-alt" />
                    <span className="menu-entry-text">
                      { t('menu.templates') }
                    </span>
                  </DropdownItem>
                  <DropdownItem
                    isHidden={corporationSettings && !corporationSettings.use_tasks}
                    isActive={pathname === '/boards'}
                    onClick={() => { push('/boards'); closeMenu(); }}
                  >
                    <SVG src={tasksIcon} />
                    <span className="menu-entry-text">
                      { t('menu.tasks') }
                    </span>
                  </DropdownItem>
                  <DropdownItem
                    isHidden={!corporationSettings.use_todo_lists}
                    isActive={pathname === '/todos'}
                    onClick={() => { push('/todos'); closeMenu(); }}
                  >
                    <SVG src={todoIcon} />
                    <span className="menu-entry-text">
                      { t('menu.todo') }
                    </span>
                  </DropdownItem>
                  <DropdownItem
                    isHidden={corporationSettings
                      && (!corporationSettings.use_contacts || !user.has_valid_license)}
                    isActive={pathname === '/contacts'}
                    onClick={() => { push('/contacts'); closeMenu(); }}
                  >
                    <SVG src={contactsIcon} />
                    <span className="menu-entry-text">
                      { t('menu.contacts') }
                    </span>
                  </DropdownItem>
                  <DropdownItem
                    isHidden={corporationSettings
                      && (!corporationSettings.use_companies || !user.has_valid_license)}
                    isActive={pathname === '/companies'}
                    onClick={() => { push('/companies'); closeMenu(); }}
                  >
                    <SVG src={companiesIcon} />
                    <span className="menu-entry-text">
                      { t('menu.companies') }
                    </span>
                  </DropdownItem>
                  <DropdownItem
                    isHidden={corporationSettings && !corporationSettings.use_resources}
                    isActive={pathname === '/resources'}
                    onClick={() => { push('/resources'); closeMenu(); }}
                  >
                    <SVG src={resourcesIcon} />
                    <span className="menu-entry-text">
                      { corporationSettings.rename_resources ? t('menu.projects') : t('menu.resources') }
                    </span>
                  </DropdownItem>
                  <Badge
                    badgeContent={totalUnreadMessages}
                    color="primary"
                    sx={{ width: '100%', '& > .MuiBadge-badge': { right: '16px', top: '12px' } }}
                  >
                    <DropdownItem
                      isActive={pathname.startsWith('/chat')}
                      style={{ width: '100%' }}
                      onClick={() => { push('/chat'); closeMenu(); }}
                    >
                      <SVG src={supportIcon} />
                      <span className="menu-entry-text">
                        { t('menu.chat') }
                      </span>
                    </DropdownItem>
                  </Badge>
                  {!!user.corporation.wiki_link && (
                  <DropdownItem
                    isActive={pathname.startsWith('/wiki')}
                    onClick={() => {
                      if (isCordova()) {
                        window.open(user.corporation.wiki_link, '_system')
                      } else {
                        push('/wiki')
                      }
                      closeMenu()
                    }}
                  >
                    <SVG src={wikiIcon} />
                    <span className="menu-entry-text">
                      {t('menu.wiki')}
                    </span>
                  </DropdownItem>
                  )}
                  <DropdownItem onClick={closeMenu} className="beamerTrigger">
                    <span className={clsx('icon', isOffline && 'is-disabled')}>
                      <i className="fas fa-bell" />
                    </span>
                    <span className="menu-entry-text">
                      { t('menu.news') }
                    </span>
                  </DropdownItem>
                </DropdownContent>
              </DropdownMenu>
            </Dropdown>
          </OutsideClickHandler>
        </div>
      </div>
    </MobileOnlyView>
  )
}
